import styled from '@emotion/styled';
import { Button, Text, colors } from '@innovationdepartment/proxima-ui';
import { BillingPlanTypeSubscription } from 'types/billing';
import BillingPlanPaymentFailed from './BillingPlan.PaymentFailed';
import { ReactNode } from 'react';

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Flex = styled.div`
  border: 1px solid ${colors.neutral10};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;

  & > div {
    align-items: flex-start;
  }
`;

const formatDate = (dateToFormat: string) =>
  new Date(dateToFormat).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const BillingPlanView = (props: BillingPlanTypeSubscription) => {
  const { handleButtonClick, billingPlanStatus, undertext } = props;

  const {
    isTrial,
    isCancelled,
    planName,
    isLocked,
    trialEnd,
    isManaged,
    isPaymentFailed,
    plan,
    planIsPending,
    hasPlanAndStillOnTrial,
    remainingDays,
    isAccountStillActive,
  } = billingPlanStatus;

  const showLocked = isLocked && !isCancelled;

  const showChoosePlan =
    isTrial || isLocked || isCancelled || hasPlanAndStillOnTrial || isAccountStillActive;
  const showCancelPlan = !isCancelled && ((Boolean(plan) && !isTrial) || hasPlanAndStillOnTrial);

  let planDetailsText: ReactNode | undefined;

  if (isCancelled && remainingDays !== undefined) {
    planDetailsText = (
      <Text variant="body2">
        Plan will continue until end of billing cycle ({remainingDays} days left)
      </Text>
    );
  }

  if (isTrial) {
    planDetailsText = (
      <Text variant="body2">
        Trial ends on <b>{formatDate(trialEnd)}</b>
      </Text>
    );
  }

  if (planIsPending && isTrial) {
    planDetailsText = (
      <>
        <Text variant="body2">
          Trial ends on <b>{formatDate(trialEnd)}</b>
        </Text>
        <Text variant="body2">Your selected plan will begin after the trial ends</Text>
      </>
    );
  }

  if (isLocked) {
    planDetailsText = <Text variant="body2">To unlock your brand, please select a plan</Text>;
  }

  const content: ReactNode = (
    <>
      <Text variant="h5">
        {planName}
        {showLocked ? ' (Locked)' : undefined}
      </Text>
      {undertext && <Text variant="body2">{undertext}</Text>}
      {planDetailsText}
    </>
  );

  return (
    <Flex>
      <Container>
        {content}
        {isPaymentFailed && <BillingPlanPaymentFailed />}
      </Container>
      {!isManaged && (
        <>
          <ButtonWrapper>
            {showChoosePlan && (
              <Button
                label="Choose plan"
                variant="contained"
                onClick={() => handleButtonClick(false)}
                size="small"
              />
            )}
            {showCancelPlan && (
              <Button
                label="Cancel plan"
                variant="outlined"
                color="secondary"
                onClick={() => handleButtonClick(true)}
                size="small"
              />
            )}
          </ButtonWrapper>
        </>
      )}
    </Flex>
  );
};

export default BillingPlanView;
